import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/checkout.css";

const PROMO_CODE = "SLOW10";
const PROMO_RATE = 0.1;

const initialFormValues = {
  contact: "",
  country: "DE",
  firstName: "",
  lastName: "",
  company: "",
  address: "",
  apartment: "",
  city: "",
  postalCode: "",
  cardNumber: "",
  expiry: "",
  securityCode: "",
  cardName: "",
  acceptedTerms: false,
};

const isValidCardNumber = (value) => {
  const digits = value.replace(/\D/g, "");
  if (digits.length < 13 || digits.length > 19) return false;

  let sum = 0;
  let shouldDouble = false;
  for (let index = digits.length - 1; index >= 0; index -= 1) {
    let digit = Number(digits[index]);
    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    shouldDouble = !shouldDouble;
  }

  return sum % 10 === 0;
};

const isValidExpiry = (value) => {
  const digits = value.replace(/\D/g, "");
  if (digits.length !== 4) return false;

  const month = Number(digits.slice(0, 2));
  const year = 2000 + Number(digits.slice(2));
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();

  return (
    month >= 1 &&
    month <= 12 &&
    (year > currentYear || (year === currentYear && month >= currentMonth))
  );
};

const formatCardNumber = (value) =>
  value
    .replace(/\D/g, "")
    .slice(0, 19)
    .replace(/(.{4})/g, "$1 ")
    .trim();

const formatExpiry = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 4);
  return digits.length > 2
    ? `${digits.slice(0, 2)} / ${digits.slice(2)}`
    : digits;
};

const createOrderNumber = () => {
  const timestamp = new Date().toISOString().replace(/\D/g, "").slice(2, 14);
  return `FP-${timestamp}-${String(Date.now()).slice(-4)}`;
};

const CheckOut = () => {
  const { cartItems, clearCart } = useContext(CartContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState(initialFormValues);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitTimerRef = useRef(null);

  useEffect(
    () => () => {
      if (submitTimerRef.current) {
        window.clearTimeout(submitTimerRef.current);
      }
    },
    []
  );

  const lines = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find((product) => product.id === item.id),
        }))
        .filter((item) => item.product),
    [cartItems]
  );

  const subtotal = lines.reduce(
    (total, item) => total + item.product.price * item.quantity,
    0
  );
  const hasPromo = location.state?.discountCode === PROMO_CODE;
  const discount = hasPromo ? subtotal * PROMO_RATE : 0;
  const shipping =
    subtotal >= siteConfig.shipping.freeThreshold
      ? 0
      : siteConfig.shipping.standardRate;
  const total = Math.max(0, subtotal - discount + shipping);

  const updateField = (field, value) => {
    setFormValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      if (!current[field]) return current;
      const nextErrors = { ...current };
      delete nextErrors[field];
      return nextErrors;
    });
  };

  const validateForm = () => {
    const nextErrors = {};
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailPattern.test(formValues.contact.trim())) {
      nextErrors.contact = "Enter a valid email address.";
    }
    if (!formValues.country) {
      nextErrors.country = "Select a delivery country.";
    }
    if (formValues.firstName.trim().length < 2) {
      nextErrors.firstName = "Enter your first name.";
    }
    if (formValues.lastName.trim().length < 2) {
      nextErrors.lastName = "Enter your last name.";
    }
    if (formValues.address.trim().length < 5) {
      nextErrors.address = "Enter a complete street address.";
    }
    if (formValues.city.trim().length < 2) {
      nextErrors.city = "Enter your city.";
    }
    if (formValues.postalCode.trim().length < 3) {
      nextErrors.postalCode = "Enter a valid postal code.";
    }
    if (!isValidCardNumber(formValues.cardNumber)) {
      nextErrors.cardNumber = "Enter a valid card number.";
    }
    if (!isValidExpiry(formValues.expiry)) {
      nextErrors.expiry = "Enter a valid future expiry date.";
    }
    if (!/^\d{3,4}$/.test(formValues.securityCode)) {
      nextErrors.securityCode = "Enter a 3 or 4 digit security code.";
    }
    if (formValues.cardName.trim().length < 3) {
      nextErrors.cardName = "Enter the name shown on the card.";
    }
    if (!formValues.acceptedTerms) {
      nextErrors.acceptedTerms = "Confirm the terms before placing the order.";
    }

    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validateForm();

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(true);
    const orderNumber = createOrderNumber();
    submitTimerRef.current = window.setTimeout(() => {
      clearCart();
      navigate("/paysuccess", {
        replace: true,
        state: { orderNumber },
      });
    }, 700);
  };

  const errorFor = (field) =>
    errors[field] ? (
      <p className="checkout-field__error" id={`checkout-${field}-error`}>
        {errors[field]}
      </p>
    ) : null;

  const describedBy = (field, hintId) =>
    [hintId, errors[field] ? `checkout-${field}-error` : ""]
      .filter(Boolean)
      .join(" ") || undefined;

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (lines.length === 0) {
    return (
      <main className="checkout-page checkout-page--empty" id="main-content">
        <section className="checkout-empty" aria-labelledby="checkout-empty-title">
          <p className="checkout-page__eyebrow">Checkout</p>
          <h1 id="checkout-empty-title">There is nothing to check out yet</h1>
          <p>Add an item to your bag before continuing to payment.</p>
          <Link to="/shop" onClick={scrollToTop}>
            Explore the collection
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="checkout-page" id="main-content">
      <div className="checkout-page__container">
        <header className="checkout-page__header">
          <p className="checkout-page__eyebrow">Secure checkout</p>
          <h1>Complete your order</h1>
          <Link to="/cartpage" onClick={scrollToTop}>
            ← Return to bag
          </Link>
        </header>

        <div className="checkout-page__layout">
          <form
            className="checkout-form"
            onSubmit={handleSubmit}
            noValidate
            aria-busy={isSubmitting}
          >
            {Object.keys(errors).length > 0 && (
              <div className="checkout-form__alert" role="alert">
                Please review the highlighted fields before placing your order.
              </div>
            )}

            <section className="checkout-section" aria-labelledby="contact-title">
              <div className="checkout-section__heading">
                <span>01</span>
                <div>
                  <h2 id="contact-title">Contact</h2>
                  <p>We will send your receipt and delivery updates here.</p>
                </div>
              </div>
              <div className="checkout-field">
                <label htmlFor="checkout-contact">Email address</label>
                <input
                  id="checkout-contact"
                  name="contact"
                  type="email"
                  value={formValues.contact}
                  onChange={(event) =>
                    updateField("contact", event.target.value)
                  }
                  autoComplete="email"
                  aria-invalid={Boolean(errors.contact)}
                  aria-describedby={describedBy("contact")}
                />
                {errorFor("contact")}
              </div>
            </section>

            <section className="checkout-section" aria-labelledby="delivery-title">
              <div className="checkout-section__heading">
                <span>02</span>
                <div>
                  <h2 id="delivery-title">Delivery address</h2>
                  <p>Use an address where someone can receive the parcel.</p>
                </div>
              </div>

              <div className="checkout-field">
                <label htmlFor="checkout-country">Country / region</label>
                <select
                  id="checkout-country"
                  name="country"
                  value={formValues.country}
                  onChange={(event) =>
                    updateField("country", event.target.value)
                  }
                  autoComplete="country"
                  aria-invalid={Boolean(errors.country)}
                  aria-describedby={describedBy("country")}
                >
                  <option value="DE">Germany</option>
                  <option value="AT">Austria</option>
                  <option value="BE">Belgium</option>
                  <option value="FR">France</option>
                  <option value="NL">Netherlands</option>
                  <option value="CH">Switzerland</option>
                  <option value="OTHER">Other European country</option>
                </select>
                {errorFor("country")}
              </div>

              <div className="checkout-fields checkout-fields--two">
                <div className="checkout-field">
                  <label htmlFor="checkout-firstName">First name</label>
                  <input
                    id="checkout-firstName"
                    name="firstName"
                    type="text"
                    value={formValues.firstName}
                    onChange={(event) =>
                      updateField("firstName", event.target.value)
                    }
                    autoComplete="given-name"
                    aria-invalid={Boolean(errors.firstName)}
                    aria-describedby={describedBy("firstName")}
                  />
                  {errorFor("firstName")}
                </div>
                <div className="checkout-field">
                  <label htmlFor="checkout-lastName">Last name</label>
                  <input
                    id="checkout-lastName"
                    name="lastName"
                    type="text"
                    value={formValues.lastName}
                    onChange={(event) =>
                      updateField("lastName", event.target.value)
                    }
                    autoComplete="family-name"
                    aria-invalid={Boolean(errors.lastName)}
                    aria-describedby={describedBy("lastName")}
                  />
                  {errorFor("lastName")}
                </div>
              </div>

              <div className="checkout-field">
                <label htmlFor="checkout-company">Company (optional)</label>
                <input
                  id="checkout-company"
                  name="company"
                  type="text"
                  value={formValues.company}
                  onChange={(event) =>
                    updateField("company", event.target.value)
                  }
                  autoComplete="organization"
                />
              </div>

              <div className="checkout-field">
                <label htmlFor="checkout-address">Street and house number</label>
                <input
                  id="checkout-address"
                  name="address"
                  type="text"
                  value={formValues.address}
                  onChange={(event) =>
                    updateField("address", event.target.value)
                  }
                  autoComplete="address-line1"
                  aria-invalid={Boolean(errors.address)}
                  aria-describedby={describedBy("address")}
                />
                {errorFor("address")}
              </div>

              <div className="checkout-field">
                <label htmlFor="checkout-apartment">
                  Apartment, suite, etc. (optional)
                </label>
                <input
                  id="checkout-apartment"
                  name="apartment"
                  type="text"
                  value={formValues.apartment}
                  onChange={(event) =>
                    updateField("apartment", event.target.value)
                  }
                  autoComplete="address-line2"
                />
              </div>

              <div className="checkout-fields checkout-fields--postal">
                <div className="checkout-field">
                  <label htmlFor="checkout-postalCode">Postal code</label>
                  <input
                    id="checkout-postalCode"
                    name="postalCode"
                    type="text"
                    value={formValues.postalCode}
                    onChange={(event) =>
                      updateField("postalCode", event.target.value)
                    }
                    autoComplete="postal-code"
                    aria-invalid={Boolean(errors.postalCode)}
                    aria-describedby={describedBy("postalCode")}
                  />
                  {errorFor("postalCode")}
                </div>
                <div className="checkout-field">
                  <label htmlFor="checkout-city">City</label>
                  <input
                    id="checkout-city"
                    name="city"
                    type="text"
                    value={formValues.city}
                    onChange={(event) =>
                      updateField("city", event.target.value)
                    }
                    autoComplete="address-level2"
                    aria-invalid={Boolean(errors.city)}
                    aria-describedby={describedBy("city")}
                  />
                  {errorFor("city")}
                </div>
              </div>
            </section>

            <section className="checkout-section" aria-labelledby="shipping-title">
              <div className="checkout-section__heading">
                <span>03</span>
                <div>
                  <h2 id="shipping-title">Shipping method</h2>
                  <p>Orders dispatch within {siteConfig.shipping.dispatchWindow}.</p>
                </div>
              </div>
              <label className="checkout-shipping-option">
                <input type="radio" name="shipping" defaultChecked />
                <span>
                  <strong>Standard tracked delivery</strong>
                  <small>Tracking details are emailed after dispatch.</small>
                </span>
                <b>{shipping === 0 ? "Free" : formatPrice(shipping)}</b>
              </label>
            </section>

            <section className="checkout-section" aria-labelledby="payment-title">
              <div className="checkout-section__heading">
                <span>04</span>
                <div>
                  <h2 id="payment-title">Payment</h2>
                  <p>This is a secure demonstration checkout. No charge is made.</p>
                </div>
              </div>

              <div className="checkout-payment">
                <div className="checkout-payment__header">
                  <strong>Credit or debit card</strong>
                  <div className="checkout-payment__marks" aria-label="Accepted cards">
                    <span>VISA</span>
                    <span>MC</span>
                    <span>AMEX</span>
                  </div>
                </div>
                <div className="checkout-payment__body">
                  <p className="checkout-payment__demo" id="card-demo-hint">
                    Demo details: 4242 4242 4242 4242, any future date and
                    any 3-digit security code.
                  </p>

                  <div className="checkout-field">
                    <label htmlFor="checkout-cardNumber">Card number</label>
                    <input
                      id="checkout-cardNumber"
                      name="cardNumber"
                      type="text"
                      value={formValues.cardNumber}
                      onChange={(event) =>
                        updateField(
                          "cardNumber",
                          formatCardNumber(event.target.value)
                        )
                      }
                      inputMode="numeric"
                      autoComplete="cc-number"
                      placeholder="0000 0000 0000 0000"
                      aria-invalid={Boolean(errors.cardNumber)}
                      aria-describedby={describedBy(
                        "cardNumber",
                        "card-demo-hint"
                      )}
                    />
                    {errorFor("cardNumber")}
                  </div>

                  <div className="checkout-fields checkout-fields--two">
                    <div className="checkout-field">
                      <label htmlFor="checkout-expiry">Expiry date</label>
                      <input
                        id="checkout-expiry"
                        name="expiry"
                        type="text"
                        value={formValues.expiry}
                        onChange={(event) =>
                          updateField("expiry", formatExpiry(event.target.value))
                        }
                        inputMode="numeric"
                        autoComplete="cc-exp"
                        placeholder="MM / YY"
                        aria-invalid={Boolean(errors.expiry)}
                        aria-describedby={describedBy("expiry")}
                      />
                      {errorFor("expiry")}
                    </div>
                    <div className="checkout-field">
                      <label htmlFor="checkout-securityCode">Security code</label>
                      <input
                        id="checkout-securityCode"
                        name="securityCode"
                        type="text"
                        value={formValues.securityCode}
                        onChange={(event) =>
                          updateField(
                            "securityCode",
                            event.target.value.replace(/\D/g, "").slice(0, 4)
                          )
                        }
                        inputMode="numeric"
                        autoComplete="cc-csc"
                        placeholder="CVC"
                        aria-invalid={Boolean(errors.securityCode)}
                        aria-describedby={describedBy("securityCode")}
                      />
                      {errorFor("securityCode")}
                    </div>
                  </div>

                  <div className="checkout-field">
                    <label htmlFor="checkout-cardName">Name on card</label>
                    <input
                      id="checkout-cardName"
                      name="cardName"
                      type="text"
                      value={formValues.cardName}
                      onChange={(event) =>
                        updateField("cardName", event.target.value)
                      }
                      autoComplete="cc-name"
                      aria-invalid={Boolean(errors.cardName)}
                      aria-describedby={describedBy("cardName")}
                    />
                    {errorFor("cardName")}
                  </div>
                </div>
              </div>

              <div className="checkout-consent">
                <label>
                  <input
                    type="checkbox"
                    checked={formValues.acceptedTerms}
                    onChange={(event) =>
                      updateField("acceptedTerms", event.target.checked)
                    }
                    aria-invalid={Boolean(errors.acceptedTerms)}
                    aria-describedby={describedBy("acceptedTerms")}
                  />
                  <span>
                    I confirm the order details and agree to the{" "}
                    <Link to="/terms" target="_blank" rel="noreferrer">
                      terms
                    </Link>{" "}
                    and{" "}
                    <Link to="/privacy" target="_blank" rel="noreferrer">
                      privacy policy
                    </Link>
                    .
                  </span>
                </label>
                {errorFor("acceptedTerms")}
              </div>
            </section>

            <button
              className="checkout-form__submit"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Placing order…" : `Pay ${formatPrice(total)}`}
            </button>
            <p className="checkout-form__footnote">
              By placing this demo order, no payment data is transmitted or
              stored.
            </p>
          </form>

          <aside className="checkout-summary" aria-labelledby="summary-title">
            <div className="checkout-summary__inner">
              <div className="checkout-summary__heading">
                <h2 id="summary-title">Your order</h2>
                <span>{lines.length} lines</span>
              </div>

              <div className="checkout-summary__items">
                {lines.map(({ product, quantity, size, color }) => (
                  <article
                    className="checkout-summary-item"
                    key={`${product.id}-${size}-${color}`}
                  >
                    <div className="checkout-summary-item__image">
                      <img
                        src={product.image || product.images?.[0]}
                        alt={product.name}
                        loading="lazy"
                        decoding="async"
                      />
                      <span aria-label={`Quantity ${quantity}`}>{quantity}</span>
                    </div>
                    <div className="checkout-summary-item__details">
                      <h3>{product.name}</h3>
                      <p>
                        {[color, size].filter(Boolean).join(" · ") ||
                          product.category}
                      </p>
                    </div>
                    <strong>{formatPrice(product.price * quantity)}</strong>
                  </article>
                ))}
              </div>

              <dl className="checkout-summary__totals">
                <div>
                  <dt>Subtotal</dt>
                  <dd>{formatPrice(subtotal)}</dd>
                </div>
                {discount > 0 && (
                  <div className="checkout-summary__discount">
                    <dt>Discount ({PROMO_CODE})</dt>
                    <dd>−{formatPrice(discount)}</dd>
                  </div>
                )}
                <div>
                  <dt>Standard delivery</dt>
                  <dd>{shipping === 0 ? "Free" : formatPrice(shipping)}</dd>
                </div>
                <div className="checkout-summary__total">
                  <dt>Total</dt>
                  <dd>{formatPrice(total)}</dd>
                </div>
              </dl>
              <p className="checkout-summary__tax">VAT included</p>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default CheckOut;
